import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Study, StudyDegreeType } from '../../types/member';
import FormField from '../common/FormField';
import { Button } from '../../design-system/components/Button';
import { Text } from '../../design-system/components/Typography';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StudyCard = styled.div`
  background: ${props => props.theme.colors.surfaceHover};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.surface};
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #c82333;
    transform: translateY(-1px);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const Checkbox = styled.input`
  margin-top: 4px;
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  cursor: pointer;
  line-height: 1.5;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

interface StudiesSectionProps {
  studies: Study[];
  onChange: (studies: Study[]) => void;
  /** When provided, Enter key in this section triggers this instead of form submit (e.g. go to next step). */
  onEnterKey?: () => void;
  fieldErrors?: Record<string, string>;
}

const StudiesSection: React.FC<StudiesSectionProps> = ({
  studies,
  onChange,
  onEnterKey,
  fieldErrors = {},
}) => {
  const { t } = useTranslation();

  const addStudy = () => {
    const newStudy: Study = {
      id: Date.now().toString(),
      titleEn: '',
      titleGr: '',
      degreeType: StudyDegreeType.Bachelors,
      institutionEn: '',
      institutionGr: '',
      isCurrent: false,
    };
    onChange([...studies, newStudy]);
  };

  const removeStudy = (id: string) => {
    onChange(studies.filter(s => s.id !== id));
  };

  const updateStudy = (id: string, updates: Partial<Study>) => {
    onChange(studies.map(s => (s.id === id ? { ...s, ...updates } : s)));
  };

  return (
    <Container
      onKeyDown={
        onEnterKey
          ? e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onEnterKey();
              }
            }
          : undefined
      }
    >
      {fieldErrors.studies && (
        <Text size="sm" color="error" role="alert">
          {fieldErrors.studies}
        </Text>
      )}
      {studies.length === 0 && (
        <Text size="sm" color="secondary">
          {t('register.studies.addStudyHint')}
        </Text>
      )}
      {studies.map(study => {
        const studyLabel =
          study.titleEn ||
          study.titleGr ||
          study.institutionEn ||
          study.institutionGr ||
          t('register.studies.studyRecord');
        return (
          <StudyCard key={study.id}>
            <RemoveButton
              onClick={() => removeStudy(study.id)}
              type="button"
              aria-label={t('register.studies.removeStudyNamed', { name: studyLabel })}
            >
              {t('register.studies.removeStudy')}
            </RemoveButton>
            <FormRow>
              <FormField
                label={t('register.studies.studyTitleEn')}
                value={study.titleEn}
                onChange={e => updateStudy(study.id, { titleEn: e.target.value })}
              />
              <FormField
                label={t('register.studies.studyTitleGr')}
                value={study.titleGr}
                onChange={e => updateStudy(study.id, { titleGr: e.target.value })}
              />
            </FormRow>
            <FormRow>
              <FormField
                label={t('register.studies.institutionEn')}
                value={study.institutionEn ?? study.institution ?? ''}
                onChange={e => updateStudy(study.id, { institutionEn: e.target.value })}
              />
              <FormField
                label={t('register.studies.institutionGr')}
                value={study.institutionGr ?? ''}
                onChange={e => updateStudy(study.id, { institutionGr: e.target.value })}
              />
            </FormRow>
            <CheckboxContainer>
              <Checkbox
                type="checkbox"
                id={`isCurrent-${study.id}`}
                checked={study.isCurrent}
                onChange={e => {
                  const isCurrent = e.target.checked;
                  updateStudy(study.id, {
                    isCurrent,
                    completionYear: isCurrent ? undefined : study.completionYear,
                    duration: isCurrent ? study.duration : undefined,
                  });
                }}
              />
              <CheckboxLabel htmlFor={`isCurrent-${study.id}`}>
                {t('register.studies.isCurrent')}
              </CheckboxLabel>
            </CheckboxContainer>
            {study.isCurrent ? (
              <FormRow>
                <FormField
                  label={t('register.studies.startYear')}
                  type="number"
                  value={study.startYear?.toString() || ''}
                  error={fieldErrors[`studies.${study.id}.startYear`]}
                  onChange={e =>
                    updateStudy(study.id, {
                      startYear: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                />
                <FormField
                  label={t('register.studies.duration')}
                  type="number"
                  min={1}
                  max={20}
                  value={study.duration?.toString() || ''}
                  error={fieldErrors[`studies.${study.id}.duration`]}
                  onChange={e =>
                    updateStudy(study.id, {
                      duration: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                />
              </FormRow>
            ) : (
              <FormRow>
                <FormField
                  label={t('register.studies.startYear')}
                  type="number"
                  value={study.startYear?.toString() || ''}
                  error={fieldErrors[`studies.${study.id}.startYear`]}
                  onChange={e =>
                    updateStudy(study.id, {
                      startYear: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                />
                <FormField
                  label={t('register.studies.completionYear')}
                  type="number"
                  value={study.completionYear?.toString() || ''}
                  error={fieldErrors[`studies.${study.id}.completionYear`]}
                  onChange={e =>
                    updateStudy(study.id, {
                      completionYear: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                />
              </FormRow>
            )}
          </StudyCard>
        );
      })}
      <Button
        onClick={addStudy}
        variant="outline"
        type="button"
        style={{ alignSelf: 'flex-start' }}
      >
        + {t('register.studies.addStudy')}
      </Button>
    </Container>
  );
};

export default StudiesSection;
